import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Send, Search, MessageCircle, Loader2, CheckCircle2, XCircle, Clock, AlertTriangle } from "lucide-react";

/**
 * Admin · WhatsApp delivery logs.
 *
 * Lists every WhatsApp template message attempted by the system —
 * filterable by mobile, template_key, and delivery status. Each row
 * exposes a "Resend" action that re-dispatches the same template +
 * variables through the standard retry chain.
 */

const STATUS_META = {
  queued:                   { label: "Queued", icon: Clock, color: "#5C6B7A" },
  sent:                     { label: "Sent", icon: CheckCircle2, color: "#1F7A3D" },
  failed:                   { label: "Failed (retrying)", icon: AlertTriangle, color: "#C77800" },
  permanently_failed:       { label: "Failed", icon: XCircle, color: "#E63946" },
  template_not_approved:    { label: "Template pending", icon: AlertTriangle, color: "#C77800" },
  template_not_found:       { label: "Unknown template", icon: XCircle, color: "#E63946" },
  skipped_no_config:        { label: "Skipped — no config", icon: AlertTriangle, color: "#C77800" },
  skipped_invalid_recipient:{ label: "Invalid recipient", icon: XCircle, color: "#E63946" },
  skipped_bad_variables:    { label: "Bad variables", icon: XCircle, color: "#E63946" },
};

export default function AdminWhatsAppLogs() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [loading, setLoading] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  // Filters
  const [mobileFilter, setMobileFilter] = useState("");
  const [templateFilter, setTemplateFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [templates, setTemplates] = useState([]);

  const load = async (p = page) => {
    setLoading(true);
    try {
      const params = { page: p, limit };
      if (mobileFilter) params.mobile = mobileFilter;
      if (templateFilter) params.template_key = templateFilter;
      if (statusFilter) params.status = statusFilter;
      const r = await api.get("/admin/whatsapp/logs", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setPage(r.data.page || 1);
    } catch (e) {
      toast.error(apiErr(e, "Failed to load logs"));
    } finally { setLoading(false); }
  };

  const loadTemplates = async () => {
    try {
      const r = await api.get("/admin/whatsapp/templates");
      setTemplates(r.data.templates || []);
    } catch { /* non-fatal */ }
  };

  useEffect(() => { load(1); loadTemplates(); /* eslint-disable-line */ }, []);

  const resend = async (logId) => {
    setResendingId(logId);
    try {
      const r = await api.post("/admin/whatsapp/resend", { log_id: logId });
      toast.success(`Resent · status: ${r.data.status}`);
      load();
    } catch (e) {
      toast.error(apiErr(e, "Resend failed"));
    } finally { setResendingId(null); }
  };

  const applyFilters = (e) => {
    e?.preventDefault();
    load(1);
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-6" data-testid="admin-whatsapp-logs-page">
      <header className="flex items-center justify-between">
        <div>
          <span className="overline">Communications</span>
          <h1 className="mt-1 font-display text-3xl font-bold text-[#0F2540]">WhatsApp Logs</h1>
          <p className="mt-1 text-sm text-[#5C6B7A]">Delivery history, retries, and manual resends.</p>
        </div>
        <button onClick={() => load(page)} disabled={loading}
          className="btn-ghost text-sm inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="wa-logs-refresh">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </header>

      {/* Filters */}
      <form onSubmit={applyFilters} className="rounded-2xl border border-[#E1E8F0] bg-white p-4 flex flex-wrap items-end gap-3" data-testid="wa-logs-filters">
        <div className="flex-1 min-w-[180px]">
          <label className="text-xs font-medium text-[#5C6B7A]">Mobile number</label>
          <input
            type="tel"
            value={mobileFilter}
            onChange={(e) => setMobileFilter(e.target.value.replace(/\D/g, ""))}
            placeholder="Last 10 digits…"
            className="mt-1 w-full rounded-lg border border-[#E1E8F0] bg-white px-3 py-2 text-sm outline-none focus:border-[#1E4F87]"
            data-testid="wa-logs-filter-mobile"
          />
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="text-xs font-medium text-[#5C6B7A]">Template</label>
          <select
            value={templateFilter}
            onChange={(e) => setTemplateFilter(e.target.value)}
            className="mt-1 w-full rounded-lg border border-[#E1E8F0] bg-white px-3 py-2 text-sm outline-none focus:border-[#1E4F87]"
            data-testid="wa-logs-filter-template"
          >
            <option value="">All templates</option>
            {templates.map((t) => (
              <option key={t.key} value={t.key}>
                {t.name} {t.enabled ? "" : "(pending approval)"}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[150px]">
          <label className="text-xs font-medium text-[#5C6B7A]">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="mt-1 w-full rounded-lg border border-[#E1E8F0] bg-white px-3 py-2 text-sm outline-none focus:border-[#1E4F87]"
            data-testid="wa-logs-filter-status"
          >
            <option value="">All statuses</option>
            {Object.entries(STATUS_META).map(([k, m]) => (
              <option key={k} value={k}>{m.label}</option>
            ))}
          </select>
        </div>
        <button type="submit" className="btn-primary text-sm inline-flex items-center gap-2 h-[38px]" data-testid="wa-logs-filter-apply">
          <Search className="h-4 w-4" /> Apply
        </button>
      </form>

      {/* Table */}
      <div className="rounded-2xl border border-[#E1E8F0] bg-white overflow-hidden">
        <table className="w-full text-sm" data-testid="wa-logs-table">
          <thead className="bg-[#F8FAFD] border-b border-[#E1E8F0]">
            <tr className="text-left text-[11px] uppercase tracking-wider text-[#5C6B7A]">
              <th className="px-4 py-3">When</th>
              <th className="px-4 py-3">Recipient</th>
              <th className="px-4 py-3">Template</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Retries</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E1E8F0]">
            {loading && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[#5C6B7A]">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[#5C6B7A]">No WhatsApp messages found.</td></tr>
            )}
            {!loading && items.map((row) => {
              const meta = STATUS_META[row.status] || { label: row.status, icon: MessageCircle, color: "#5C6B7A" };
              const Icon = meta.icon;
              return (
                <tr key={row.log_id} className="hover:bg-[#FAFBFD]" data-testid={`wa-log-row-${row.log_id}`}>
                  <td className="px-4 py-3 text-[#0F2540]">{fmtDate(row.created_at)}</td>
                  <td className="px-4 py-3 font-mono text-[#0F2540]">+{row.recipient || row.raw_recipient}</td>
                  <td className="px-4 py-3">
                    <div className="text-[#0F2540] font-medium">{row.template_name}</div>
                    <div className="text-[11px] text-[#5C6B7A] font-mono">{row.template_key}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: meta.color }}>
                      <Icon className="h-3.5 w-3.5" /> {meta.label}
                    </span>
                    {row.error && (
                      <div className="text-[11px] text-[#5C6B7A] mt-0.5 truncate max-w-[280px]" title={row.error}>{row.error}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[#0F2540]">{row.retries || 0}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => resend(row.log_id)}
                      disabled={resendingId === row.log_id}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-[#1E4F87] hover:underline disabled:opacity-50"
                      data-testid={`wa-log-resend-${row.log_id}`}
                    >
                      {resendingId === row.log_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                      Resend
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm" data-testid="wa-logs-pagination">
          <span className="text-[#5C6B7A]">Showing page {page} of {totalPages} · {total} total</span>
          <div className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => load(page - 1)} className="btn-ghost text-xs disabled:opacity-50" data-testid="wa-logs-prev">Previous</button>
            <button disabled={page >= totalPages} onClick={() => load(page + 1)} className="btn-ghost text-xs disabled:opacity-50" data-testid="wa-logs-next">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}
